// The financial reports (PRD rev. 1.22, FR1–FR8): cash flow, spending by category, income by client,
// and the flow from clients to categories, over QuickBooks's synced lines. Pure, so the browser
// regroups and filters without a round trip (as Profitability and Cash do), and the rules are tested.

import { addMonths, monthOf, type Iso, type Month } from "./dates";
import { NOT_ASSIGNED, UNMAPPED, type BooksLine, type PeopleGroup, type SpendCategory } from "./books";
import type { CodingSource, CostTreatment } from "./cost-coding";

export type ReportGrouping = "month" | "quarter" | "year";

/** A synced line as the reports read it: its client or category resolved, and for People, its person. */
export interface ReportLine extends BooksLine {
  clientName?: string;
  categoryId?: string;
  /** The top-level category, for spending. */
  topId?: string;
  /** People's subcategory group and the person (or benefit) the line is for (QB6). */
  peopleGroup?: PeopleGroup;
  person?: string;
  /** For an Employees line: shares by person, allocated from Base Camp pay (QB6). */
  allocation?: { person: string; share: number }[];
  assigned?: boolean;
  /** Spending's project and who bears it (FR10), and the key its coding is kept under. */
  project?: string;
  treatment?: CostTreatment;
  codingSource?: CodingSource;
  codingKey?: string;
  /** Income matched as the rebill of a billed-back cost (FR10.4). */
  rebill?: boolean;
}

/** QuickBooks's own totals for one month and basis, read from its report's summaries (FR8). */
export interface BooksTotals {
  income: number;
  expense: number;
}

export function periodOf(day: Iso, grouping: ReportGrouping): string {
  const m = monthOf(day);
  if (grouping === "month") return m;
  if (grouping === "year") return m.slice(0, 4);
  return `${m.slice(0, 4)}-Q${Math.floor((Number(m.slice(5, 7)) - 1) / 3) + 1}`;
}

/** Every period from `from` to `to` (months, inclusive), in order. */
export function periodsBetween(from: Month, to: Month, grouping: ReportGrouping): string[] {
  const out: string[] = [];
  for (let m = from; m <= to && out.length < 400; m = addMonths(m, 1)) {
    const p = periodOf(`${m}-01`, grouping);
    if (out.at(-1) !== p) out.push(p);
  }
  return out;
}

/** The range just before one of the same length, or the same range a year earlier (FR6). */
export function compareRange(from: Month, to: Month, mode: "previous" | "year"): { from: Month; to: Month } {
  if (mode === "year") return { from: addMonths(from, -12), to: addMonths(to, -12) };
  const n = monthsIn(from, to);
  return { from: addMonths(from, -n), to: addMonths(from, -1) };
}
const monthsIn = (from: Month, to: Month) => (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + Number(to.slice(5, 7)) - Number(from.slice(5, 7)) + 1;

export interface Slice {
  key: string;
  label: string;
  amount: number;
}

const sortSlices = (m: Map<string, Slice>) => [...m.values()].filter((s) => Math.abs(s.amount) >= 0.005).sort((a, b) => b.amount - a.amount);

/** Income by client (FR5). Lines with no client sit under Not yet assigned (QB8). */
export function incomeByClient(lines: readonly ReportLine[]): Slice[] {
  const m = new Map<string, Slice>();
  for (const l of lines) {
    if (l.kind !== "income" || l.transfer) continue;
    const k = l.clientName ?? NOT_ASSIGNED;
    const s = m.get(k) ?? { key: k, label: k, amount: 0 };
    s.amount += l.amount;
    m.set(k, s);
  }
  return sortSlices(m);
}

/**
 * Spending at one level (FR4): top categories; or, inside one, its subcategories and its own accounts;
 * inside People's subcategories, each person; inside any other leaf, each account.
 */
export function spendingAt(lines: readonly ReportLine[], categories: readonly SpendCategory[], within?: string): Slice[] {
  const name = (id: string) => categories.find((c) => c.id === id)?.name ?? (id === UNMAPPED ? "Unmapped" : id);
  const node = within ? categories.find((c) => c.id === within) : undefined;
  const children = within ? categories.filter((c) => c.parentId === within) : [];
  const m = new Map<string, Slice>();
  const add = (key: string, label: string, amount: number) => {
    const s = m.get(key) ?? { key, label, amount: 0 };
    s.amount += amount;
    m.set(key, s);
  };
  for (const l of lines) {
    if (l.kind !== "expense" || l.transfer) continue;
    const cat = l.categoryId ?? UNMAPPED;
    if (!within) {
      const top = l.topId ?? cat;
      add(top, name(top), l.amount);
    } else if (children.length && l.topId === within) {
      // A line mapped to the parent itself shows as the parent's own, beside its subcategories.
      if (cat === within) add(`acct:${l.accountId}`, l.accountName, l.amount);
      else add(cat, name(cat), l.amount);
    } else if (cat === within) {
      if (node?.people === "employees" && l.allocation?.length) {
        for (const a of l.allocation) add(`person:${a.person}`, a.person, l.amount * a.share);
      } else if (node?.people) {
        const who = l.person ?? l.accountName;
        add(`person:${who}`, who, l.amount);
      } else add(`acct:${l.accountId}`, l.accountName, l.amount);
    }
  }
  return sortSlices(m);
}

/** The lines behind one slice of spending or income, for the drill-down's last level. */
export function linesFor(lines: readonly ReportLine[], kind: "income" | "expense", key: string, within?: string): ReportLine[] {
  return lines.filter((l) => {
    if (l.kind !== kind || l.transfer) return false;
    if (kind === "income") return (l.clientName ?? NOT_ASSIGNED) === key;
    if (key.startsWith("acct:")) return l.accountId === key.slice(5) && (!within || l.categoryId === within || l.topId === within);
    if (key.startsWith("person:")) {
      const p = key.slice(7);
      return l.categoryId === within && (l.person === p || l.allocation?.some((a) => a.person === p) || (!l.person && !l.allocation?.length && l.accountName === p));
    }
    return l.categoryId === key || l.topId === key;
  });
}

/** One project's spending by who bears it (FR10.5): internal codes and No project are rows of their own. */
export interface ProjectSpend {
  /** The project code, or "" for No project. */
  code: string;
  project: number;
  billed: number;
  company: number;
  total: number;
}

/** Spending by project and treatment (FR10.5), largest first, with No project last. */
export function spendingByProject(lines: readonly ReportLine[]): ProjectSpend[] {
  const by = new Map<string, ProjectSpend>();
  for (const l of lines) {
    if (l.kind !== "expense" || l.transfer) continue;
    const code = l.project ?? "";
    const row = by.get(code) ?? { code, project: 0, billed: 0, company: 0, total: 0 };
    const t = l.treatment ?? "company";
    row[t === "billed" ? "billed" : t === "project" ? "project" : "company"] += l.amount;
    row.total += l.amount;
    by.set(code, row);
  }
  return [...by.values()].sort((a, b) => (a.code === "" ? 1 : b.code === "" ? -1 : b.total - a.total));
}

export interface FlowPeriod {
  key: string;
  in: number;
  out: number;
  net: number;
  /** From the forecast, not QuickBooks. */
  forecast: boolean;
}

/** Money in and out per period (FR2): QuickBooks's lines for the past, the forecast's for the future. */
export function cashFlowPeriods(
  lines: readonly ReportLine[],
  forecast: readonly { date: Iso; direction: "in" | "out"; amount: number }[],
  from: Month,
  to: Month,
  grouping: ReportGrouping,
): FlowPeriod[] {
  const keys = periodsBetween(from, to, grouping);
  const byKey = new Map(keys.map((k) => [k, { key: k, in: 0, out: 0, net: 0, forecast: false }]));
  for (const l of lines) {
    const p = byKey.get(periodOf(l.date, grouping));
    if (!p || l.transfer) continue;
    if (l.kind === "income") p.in += l.amount;
    else p.out += l.amount;
  }
  for (const f of forecast) {
    const p = byKey.get(periodOf(f.date, grouping));
    if (!p) continue;
    p.forecast = true;
    if (f.direction === "in") p.in += f.amount;
    else p.out += f.amount;
  }
  for (const p of byKey.values()) p.net = p.in - p.out;
  return keys.map((k) => byKey.get(k)!);
}

/** One link of the flow chart (FR3): from a client into Income, or from Income out to a category. */
export interface FlowLink {
  from: string;
  to: string;
  amount: number;
}

/**
 * Clients → Income → categories (FR3), the smallest folded into Other so the chart stays legible.
 * What income doesn't spend flows on as Saved; spending beyond income comes in From reserves.
 */
export function flowLinks(lines: readonly ReportLine[], categories: readonly SpendCategory[], maxClients = 8): FlowLink[] {
  const clients = incomeByClient(lines);
  const shown = clients.slice(0, maxClients);
  const rest = clients.slice(maxClients).reduce((t, s) => t + s.amount, 0);
  const spend = spendingAt(lines, categories);
  const income = clients.reduce((t, s) => t + s.amount, 0);
  const out = spend.reduce((t, s) => t + s.amount, 0);
  const links: FlowLink[] = shown.filter((s) => s.amount > 0).map((s) => ({ from: s.label, to: "Income", amount: s.amount }));
  if (rest > 0) links.push({ from: "Other clients", to: "Income", amount: rest });
  if (out > income) links.push({ from: "From reserves", to: "Income", amount: out - income });
  for (const s of spend) if (s.amount > 0) links.push({ from: "Income", to: s.label, amount: s.amount });
  if (income > out) links.push({ from: "Income", to: "Saved", amount: income - out });
  return links;
}

/** The tie-out (FR8): Base Camp's sums against QuickBooks's own report totals, by month. */
export function tieOut(lines: readonly ReportLine[], totals: Readonly<Record<Month, BooksTotals | undefined>>): { income: number; expense: number; booksIncome: number; booksExpense: number; months: number; missing: Month[] } {
  let income = 0;
  let expense = 0;
  let booksIncome = 0;
  let booksExpense = 0;
  const missing: Month[] = [];
  for (const l of lines) if (!l.transfer) l.kind === "income" ? (income += l.amount) : (expense += l.amount);
  const months = Object.keys(totals);
  for (const m of months) {
    const t = totals[m] ?? undefined;
    if (!t) missing.push(m);
    else {
      booksIncome += t.income;
      booksExpense += t.expense;
    }
  }
  return { income, expense, booksIncome, booksExpense, months: months.length, missing };
}

/** The CSV of a report's lines (FR7), with the exporter and time on the first line (SEC12). */
export function reportCsv(lines: readonly ReportLine[], note: string, categoryName: (id: string) => string): string {
  const cell = (v: unknown) => {
    const s = v === undefined || v === null ? "" : String(v);
    // A leading = + - @ would run as a formula in a spreadsheet: quote it inert.
    const safe = /^[=+\-@]/.test(s) && !/^-?\d/.test(s) ? `'${s}` : s;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const head = ["Date", "Kind", "Transaction", "Number", "Payee or payer", "Account", "Client", "Category", "Person", "Memo", "Amount", "Assigned in Base Camp"];
  const rows = lines.map((l) => [
    l.date,
    l.kind === "income" ? "Income" : "Spending",
    l.txnType,
    l.docNumber,
    l.partyName,
    l.accountName,
    l.kind === "income" ? (l.clientName ?? NOT_ASSIGNED) : "",
    l.kind === "expense" ? categoryName(l.categoryId ?? UNMAPPED) : "",
    l.person ?? (l.allocation?.length ? "Allocated" : ""),
    l.memo,
    l.amount.toFixed(2),
    l.assigned ? "Yes" : "",
  ]);
  return [`# ${note}`, head, ...rows].map((r) => (Array.isArray(r) ? r.map(cell).join(",") : r)).join("\n");
}
